import React from 'react';
import { motion } from 'framer-motion';
import { FiPackage } from 'react-icons/fi';

const LoadingScreen = ({ isLoading = true, hasProject = false }) => {
  if (!isLoading && !hasProject) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: 'linear-gradient(135deg, var(--bg-app-start), var(--bg-app-end))', color: 'var(--text-primary)' }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-2xl"
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-8"
          >
            <FiPackage className="w-20 h-20 mx-auto" style={{ color: 'var(--accent)' }} />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-4xl font-bold mb-4"
            style={{ color: 'var(--text-primary)' }}
          >
            AE Data Manager
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-lg mb-8"
            style={{ color: 'var(--text-secondary)' }}
          >
            A browser-based, offline-first AE inspection file management tool
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="rounded-lg shadow-lg p-6 text-left"
            style={{ background: 'var(--surface-1)', border: '1px solid var(--border-color)' }}
          >
            <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Getting Started</h2>
            <div className="space-y-3" style={{ color: 'var(--text-secondary)' }}>
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: 'var(--accent-soft)' }}>
                  <span className="font-semibold text-sm" style={{ color: 'var(--accent)' }}>1</span>
                </div>
                <div>
                  <p className="font-medium" style={{ color: 'var(--text-primary)' }}>Click "Open Inspection Folder" in the header</p>
                  <p className="text-sm">Select your AE inspection project folder using the File System Access API</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: 'var(--accent-soft)' }}>
                  <span className="font-semibold text-sm" style={{ color: 'var(--accent)' }}>2</span>
                </div>
                <div>
                  <p className="font-medium" style={{ color: 'var(--text-primary)' }}>Browse and edit your files</p>
                  <p className="text-sm">View files in the explorer and click the edit button to modify them</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: 'var(--accent-soft)' }}>
                  <span className="font-semibold text-sm" style={{ color: 'var(--accent)' }}>3</span>
                </div>
                <div>
                  <p className="font-medium" style={{ color: 'var(--text-primary)' }}>Save changes directly to your files</p>
                  <p className="text-sm">All edits are saved in-place to your original project folder</p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-8 text-sm"
            style={{ color: 'var(--text-muted)' }}
          >
            <p>Works offline • No server required • Local-first architecture</p>
          </motion.div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, var(--bg-app-start), var(--bg-app-end))', color: 'var(--text-primary)' }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="text-center"
      >
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="mb-8"
        >
          <FiPackage className="w-16 h-16 mx-auto" style={{ color: 'var(--accent)' }} />
        </motion.div>
        
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-3xl font-bold mb-2"
          style={{ color: 'var(--text-primary)' }}
        >
          AE Data Manager
        </motion.h1>
        
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-8"
          style={{ color: 'var(--text-secondary)' }}
        >
          Loading your data management workspace...
        </motion.p>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="flex justify-center"
        >
          <div className="loading-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default LoadingScreen;
